import { Link } from 'react-router-dom';
import { useVehicles } from '../../../hooks/useVehicles';
import { useMemo } from 'react';

export function FeaturedVehicles() {
  const { vehicles, loading } = useVehicles();

  // Derived synchronously from vehicles — no need for useEffect + useState
  const filteredVehicles = useMemo(() =>
    (vehicles || [])
      .filter((v: any) => v.price <= 30000 && v.price >= 15000)
      .sort((a: any, b: any) => b.price - a.price)
      .slice(0, 6),
    [vehicles]
  );

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1C2B1E]"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Featured Vehicles Under $30K
          </h2>
          <p className="text-lg text-gray-600">
            Hand-picked quality vehicles that offer the best value in their price range
          </p>
        </div>

        {filteredVehicles.length > 0 ? (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto mb-8">
              {filteredVehicles.map((vehicle) => (
                <Link
                  key={vehicle.id}
                  to={`/inventory/${vehicle.uuid || vehicle.id}`}
                  className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow overflow-hidden group"
                >
                  <div className="relative h-48 bg-gray-200 overflow-hidden">
                    {vehicle.images && vehicle.images.length > 0 ? (
                      <img
                        src={vehicle.images[0]}
                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} — ${vehicle.mileage?.toLocaleString()} miles`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-car-line text-6xl text-gray-400"></i>
                      </div>
                    )}
                    <div className="absolute top-3 right-3 bg-[#1C2B1E] text-white px-3 py-1 rounded-full text-sm font-semibold">
                      ${vehicle.price.toLocaleString()}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-bold mb-2">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </h3>
                    <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-dashboard-line"></i>
                        {vehicle.mileage?.toLocaleString()} mi
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-settings-line"></i>
                        {vehicle.transmission}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-gas-station-line"></i>
                        {vehicle.fuelType}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#1C2B1E] font-semibold group-hover:text-[#E8B84B] transition-colors">
                        View Details
                      </span>
                      <i className="ri-arrow-right-line text-[#1C2B1E]"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center">
              <Link
                to="/inventory"
                className="inline-flex items-center gap-2 bg-[#1C2B1E] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#2C3E55] transition-colors"
              >
                View All Inventory
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <i className="ri-car-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-xl text-gray-600 mb-6">
              Check back soon for new arrivals under $30K
            </p>
            <Link
              to="/inventory"
              className="inline-flex items-center gap-2 bg-[#1C2B1E] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#2C3E55] transition-colors"
            >
              Browse All Inventory
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
